<template>
    <navbar :class="{ 'no-permissions': hasNoPermissions }" :show-navbar="showNavbar">
        <div v-if="hasNoPermissions" class="pt-3 d-flex align-items-center" style="gap: 20px;">
            <h4 class="text-white bg-danger rounded p-2 d-inline m-0">
                <i class="fa fa-warning"></i>
                User <strong>{{ $user.userName }}</strong> has no permissions to Scriptly
            </h4>
            <div title="Log Out" v-b-tooltip.hover @click="logOut" class="action-btn">
                <i class="fas fa-sign-out-alt fa-fw fa-2x" />
            </div>
        </div>
        <template v-else>
            <div id="user-stats">
                <b-button variant="link" @click="toggleProductivity">
                    <span>Productivity Stats (Month To Date) </span>
                    <i v-if="!viewProductivity" class="fas fa-arrow-down" />
                </b-button>
                <b-collapse :visible="viewProductivity" class="ml-1">
                    <div v-if="userProductivity.isPharmacist && !isLoading" class="stats">
                        <!-- Pharmacist Stats -->
                        <div v-if="userProductivity.employee" v-b-tooltip.hover title="You">
                            <i class="fas fa-user"></i>
                            {{ userProductivity.employee || '' }}
                        </div>

                        <div v-b-tooltip.hover title="PV2">
                            <b-icon icon="file-earmark-check-fill" />
                            {{ userProductivity.pv2 || '0' }}
                        </div>

                        <div v-b-tooltip.hover title="PV2 Cancelled">
                            <b-icon icon="x-octagon-fill" />
                            {{ userProductivity.cancelled || '0' }}
                        </div>

                        <div v-b-tooltip.hover title="Consultations">
                            <i class="fas fa-glasses" />
                            {{ userProductivity.consultations || '0' }}
                        </div>

                        <div v-b-tooltip.hover title="Transfers">
                            <i class="fas fa-exchange-alt" />
                            {{ userProductivity.transferStat || '0' }}
                        </div>

                        <div v-b-tooltip.hover title="PV1">
                            <b-icon icon="file-earmark-check" />
                            {{ userProductivity.pv1 || '0' }}
                        </div>

                        <div v-b-tooltip.hover title="Dispense Errors Created">
                            <i class="fas fa-exclamation-circle" />
                            {{ userProductivity.dispenseErrorsCreated || '0' }}
                        </div>

                        <div v-b-tooltip.hover title="Productivity Score">
                            <i class="fas fa-star-half-alt" />
                            {{ (userProductivity.productivityReport || 0).toFixed(0) }}
                        </div>
                    </div>
                    <div v-if="!userProductivity.isPharmacist && !isLoading" class="stats">
                        <!-- Technician Stats -->
                        <div v-if="userProductivity.employee" v-b-tooltip.hover title="You">
                            <i class="fas fa-user" />
                            {{ userProductivity.employee || '' }}
                        </div>

                        <div v-b-tooltip.hover title="Non E-Scripts (Refills)">
                            <i class=" fas fa-file-signature" />
                            {{ userProductivity.nonEscripts || '0' }}
                            ({{ userProductivity.nonEscriptsRefills || '0' }})
                        </div>

                        <div v-b-tooltip.hover title="Escripts (Refills)">
                            <i class=" fas fa-file-upload" />
                            {{ userProductivity.escripts || '0' }}
                            ({{ userProductivity.escriptsRefills || '0' }})
                        </div>

                        <div v-b-tooltip.hover title="Total Scripts">
                            <i class=" fas fa-file-prescription" />
                            {{ userProductivity.total || '0' }}
                        </div>

                        <div v-b-tooltip.hover :title="scriptErrorsTooltip">
                            <i class=" fas fa-file-excel" />
                            {{ scriptErrors || '0' }}
                        </div>

                        <div v-b-tooltip.hover :title="scriptsWithoutErrorsTooltip">
                            <i class=" fas fa-file-medical-alt" />
                            {{ scriptsWithoutErrors || '0' }}
                        </div>

                        <div v-b-tooltip.hover :title="scriptErrorRateTooltip">
                            <i class=" fas fa-tools" />
                            {{ (scriptErrorRate * 100).toFixed(2) + '%' }}
                        </div>

                        <div v-b-tooltip.hover title="Total Calls">
                            <i class=" fas fa-phone" />
                            {{ userProductivity.totalCalls || '0' }}
                        </div>

                        <div v-b-tooltip.hover title="Inbound Calls">
                            <i class=" fas fa-headphones" />
                            {{ userProductivity.inbound || '0' }}
                        </div>

                        <div v-b-tooltip.hover title="Outbound Calls">
                            <i class=" fas fa-microphone" />
                            {{ userProductivity.outbound || '0' }}
                        </div>

                        <div v-b-tooltip.hover title="Productivity Score">
                            <i class=" fas fa-star-half-alt" />
                            {{ (userProductivity.productivityReport || 0).toFixed(0) }}
                        </div>

                    </div>
                    <div v-if="isLoading">
                        <b-spinner label="Spinning" class="mr-2 productivity-spinner" />
                        <span>Loading productivity stats</span>
                    </div>
                </b-collapse>
            </div>
            <div class="d-flex align-items-center search" data-tour="topnav.global-search"
                @click="focusGlobalSearch">
                <i class="fas fa-search" />
                <b-input id="global-search-field" ref="globalSearchField" type="search" style="width: 150px;"
                    placeholder="Search" v-model="globalQuery" autocomplete="off"
                    @keydown.down.prevent="navigateGlobalSearch('down')"
                    @keydown.up.prevent="navigateGlobalSearch('up')"
                    @keydown.enter.prevent="selectGlobalSearchItem"
                    @keydown.esc.prevent="handleEscapeKey" />
                <b-popover ref="globalSearchPopover" target="#global-search-field" triggers="focus" custom-class="sidebar-global-search"
                    placement="bottom">
                    <GlobalSearch ref="globalSearch" :query="globalQuery"
                        :selected-index="globalSearchSelectedIndex"
                        @selected="handleGlobalSearchSelected"
                        @navigation-change="globalSearchSelectedIndex = $event" />
                </b-popover>
            </div>
            <div class="actions">
                <div class="tasks action-btn" data-tour="topnav.tasks"
                    v-b-tooltip.hover.html
                    :title="taskTooltip">
                    <router-link :to="{ name: 'Tasks' }">
                        <span :class="{ 'tasks-icon': true, 'highlight-count': totalTaskCount }">
                            <i class="fas fa-check-double fa-fw" />
                            <div v-show="totalTaskCount" class="task-barbell">
                                <span class="barbell-count barbell-individual" :class="{ dimmed: !taskCount.individual }">
                                    {{ (taskCount.individual || 0) > 99 ? '99+' : (taskCount.individual || 0) }}
                                </span>
                                <span class="barbell-connector"></span>
                                <span class="barbell-count barbell-team" :class="{ dimmed: !taskCount.team }">
                                    {{ (taskCount.team || 0) > 99 ? '99+' : (taskCount.team || 0) }}
                                </span>
                            </div>
                        </span>
                    </router-link>
                </div>

                <div class="dispense-errors action-btn" data-tour="topnav.dispense-errors"
                    :title="errorCounter ? 'You have ' + dispenseErrorLabel + 's assigned!' : 'No ' + dispenseErrorLabel + 's'"
                    v-b-tooltip.hover>
                    <router-link :to="{ name: 'DispenseErrors', params: { onlyAssigned: true } }">
                        <span :class="{ 'dispense-error-notification-icon': true, 'highlight-count': errorCounter }">
                            <i class="fas fa-exclamation-triangle fa-fw" />
                            <div id="error-counter" v-show="errorCounter" class="action-count-badge danger">
                                {{ errorCounter > 99 ? "99+" : errorCounter }}
                            </div>
                        </span>
                    </router-link>
                </div>

                <div title="Drug Price Test" v-b-tooltip.hover @click="showPriceTestModal = true"
                    v-b-modal.toolbox-modal class="action-btn" data-tour="topnav.price-test">
                    <i class="fas fa-dollar-sign fa-fw" />
                </div>

                <div title="Prospective DUR" v-b-tooltip.hover @click="showProspectiveDURModal = true"
                    class="action-btn" data-tour="topnav.dur">
                    <i class="fas fa-pills fa-fw" />
                </div>

                <div class="notifications action-btn" data-tour="topnav.notifications"
                    title="Notifications" v-b-tooltip.hover>
                    <b-popover ref="notificationPopover" target="notification-bell" triggers="click blur"
                        placement="bottom" custom-class="notification-popover">
                        <NotificationsList small unread-only view-all-link scroll :page-size="10"
                            @navigate="$root.$emit('bv::hide::popover')" />
                    </b-popover>

                    <button id="notification-bell"
                        :class="{ 'notification-icon': true, 'highlight-count': notificationCounter }" type="button">
                        <i :class="{ 'fas fa-bell fa-fw': true, 'shake': notificationShouldShake }" />
                        <div id="notification-counter" v-show="notificationCounter > 0" class="action-count-badge">
                            {{ notificationCounter > 99 ? "99+" : notificationCounter }}
                        </div>
                    </button>
                </div>

                <div class="action-btn auto-mode-btn" data-tour="topnav.auto-mode"
                    :title="isAutoModeActive ? 'Auto Mode Active - Click or press F1 to advance to next item (Shift+ESC to exit)' : 'Auto Mode (F1)'"
                    v-b-tooltip.hover
                    @click="triggerAutoMode">
                    <button
                        id="auto-mode-icon"
                        :class="{ 'auto-mode-icon': true, 'auto-mode-active': isAutoModeActive }"
                        type="button">
                        <i class="fas fa-rocket fa-fw" />
                    </button>
                </div>

                <div title="Log Out" v-b-tooltip.hover @click="logOut" class="action-btn"
                    data-tour="topnav.log-out">
                    <i class="fas fa-sign-out-alt fa-fw" />
                </div>
                <PriceTestModal v-if="showPriceTestModal" @hide="showPriceTestModal = false" />
                <ProspectiveDURModal v-if="showProspectiveDURModal" @hide="showProspectiveDURModal = false"
                    :preloaded-patient-id="getCurrentPatientIdFromRoute()" />
                <b-modal v-model="showPatientRecentlyViewedModal" hide-footer no-fade centered
                    title="Recently Viewed Patients">
                    <PatientRecentlyViewed :keyboard-nav="true" :no-items-message="true"
                        @change="$router.push('/processing/patient-dashboard/' + $event.id); showPatientRecentlyViewedModal = false;" />
                </b-modal>
                <AutoModeConfirmModal
                    :show="showAutoModeConfirmModal"
                    @update:show="showAutoModeConfirmModal = $event"
                    @confirmed="onAutoModeConfirmed"
                    @cancelled="onAutoModeCancelled" />
            </div>
        </template>
    </navbar>
</template>

<script lang='ts'>
import { Component, Vue, Watch, Mixins } from 'vue-property-decorator';
import axios from "axios";
import Navbar from "@/components/Navbar/Navbar.vue";
import NavbarToggleButton from "@/components/Navbar/NavbarToggleButton.vue";
import { UserProductivity } from "@/models/UserProductivity";
import { ProductivityStatsBase } from "@/models/ProductivityStatsBase";
import { PharmacistProductivity } from "@/models/PharmacistProductivity";
import { logoutCognito } from "@/util/cognitoAuth";
import MicrosoftAuth from "@/util/microsoftAuth";
import userInfoService from "@/util/userInfoService";
import AutoModeHotkeyMixin from '@/mixins/AutoModeHotkeyMixin';
import AutoModeConfirmModal from '@/components/AutoMode/AutoModeConfirmModal.vue';
import { getUserSetting, saveUserSetting } from "@/services/UserSettingsService";

@Component({
    name: 'TopNavbar',
    components: {
        Navbar,
        NavbarToggleButton,
        PriceTestModal: () => import('@/components/Drug/PriceTestModalComponent.vue'),
        ProspectiveDURModal: () => import('@/components/Drug/ProspectiveDURModalComponent.vue'),
        NotificationsList: () => import("@/components/Notifications/NotificationsList.vue"),
        PatientRecentlyViewed: () => import("@/components/Patient/PatientRecentlyViewed.vue"),
        GlobalSearch: () => import("@/components/GlobalSearch.vue"),
        AutoModeConfirmModal,
    },
})
export default class TopNavbar extends Mixins(AutoModeHotkeyMixin) {
    private activeNotifications = false;
    private showNavbar = false;
    protected userProductivity = {};
    protected viewProductivity = false;
    protected errorCounter = 0;
    protected notificationCounter = 0;
    protected taskCount: { individual: number; team: number } = { individual: 0, team: 0 };

    get totalTaskCount(): number {
        if (typeof this.taskCount === 'number') return this.taskCount;
        return (this.taskCount?.individual ?? 0) + (this.taskCount?.team ?? 0);
    }

    get taskTooltip(): string {
        const individual = this.taskCount?.individual ?? 0;
        const team = this.taskCount?.team ?? 0;
        return `Tasks<br>Assigned to Me: ${individual}<br>Assigned to Teams: ${team}`;
    }
    protected isLoading = false;
    protected showPriceTestModal = false;
    protected showProspectiveDURModal = false;
    protected notificationShouldShake = false;
    protected showPatientRecentlyViewedModal = false;
    protected showGlobalSearchModal = false;

    protected globalQuery = "";
    protected globalSearchSelectedIndex = -1;

    @Watch('globalQuery')
    onGlobalQueryChange() {
        this.globalSearchSelectedIndex = -1;
    }

    get routeName(): string {
        const { name } = this.$route;
        return this.capitalizeFirstLetter(name);
    }

    get hasNoPermissions() {
        return this.$user.userName && !this.$permissions?.length;
    }

    get isAutoModeActive(): boolean {
        return this.$store.autoModeEnabled || false;
    }

    triggerAutoMode(): void {
        console.log('Auto Mode button clicked');

        // If already in auto mode, directly advance to next item
        if (this.$store.autoModeEnabled) {
            console.log('Already in auto mode, advancing to next item...');
            this.handleF1Press();
        } else {
            // Show confirmation modal when entering auto mode
            console.log('Showing Auto Mode confirmation modal...');
            this.showAutoModeConfirmModal = true;
            console.log('showAutoModeConfirmModal set to:', this.showAutoModeConfirmModal);
        }
    }

    exitAutoMode(): void {
        // Clear auto mode state
        this.$store.autoModeEnabled = false;
        this.$store.currentAutoModeQueue = null;
        this.$store.autoModeSessionId = null;
        this.$store.lastAutoModeResponse = null;

        this.$notification({
            message: 'Auto Mode has been exited',
            timeout: 3000,
            icon: "now-ui-icons ui-1_bell-53",
            horizontalAlign: "center",
            verticalAlign: "top",
            type: "info"
        });
    }

    @Watch('routeName') changedRoute() {
        this.getProductivity();
    }

    created() {
        this.isLoading = true;
        this.getProductivity();
        this.fetchDispenseErrorLabel();
        this.fetchShowErrorEventStats();
        if (getUserSetting("ViewProductivityStats") == null || getUserSetting("ProductivityStatsDate") != new Date().toLocaleDateString()) {
            saveUserSetting("ViewProductivityStats", 'true');
            this.viewProductivity = true;
        }
        if (getUserSetting("ViewProductivityStats") == 'true') {
            this.viewProductivity = true;
        }

        this.$http.get('DispenseError/error-count')
            .then(res => {
                this.errorCounter = res?.data || 0;
            })
            .catch(err => {
                console.error('Error while pulling number of errors assigned.', {
                    err,
                    response: err?.response,
                });
            });

        //Prescription Error Counter live connection setup
        this.$watch("$root.$data.dispenseErrorCount", () => {
            this.errorCounter = this.$root.$data.dispenseErrorCount;
        });

        this.$watch("$root.$data.notificationCount", () => {
            this.notificationCounter = this.$root.$data.notificationCount;
        });

        // Initialize from cached data if already available
        if (this.$root.$data.notificationCount !== undefined) {
            this.notificationCounter = this.$root.$data.notificationCount;
        }

        this.$watch("$root.$data.taskCount", () => {
            this.taskCount = this.$root.$data.taskCount;
        });

        // Initialize from cached data if already available
        if (this.$root.$data.taskCount !== undefined) {
            this.taskCount = this.$root.$data.taskCount;
        }

        document.addEventListener("keydown", this.preventF1Default);
        document.addEventListener("keyup", this.autoModeHotkeyListener);
        document.addEventListener("keyup", this.hotKeyListener);
    }

    beforeDestroy() {
        document.removeEventListener("keydown", this.preventF1Default);
        document.removeEventListener("keyup", this.autoModeHotkeyListener);
        document.removeEventListener("keyup", this.hotKeyListener);
    }

    preventF1Default(e: KeyboardEvent) {
        // Prevent browser help from opening when F1 is pressed
        if (e.code === 'F1') {
            e.preventDefault();
        }
    }

    autoModeHotkeyListener(e: KeyboardEvent): void {
        if (e.code !== "F1") return;

        // A guided tour owns the keyboard while it runs, and Auto Mode would navigate the page
        // out from under the step being shown.
        if (this.$root.$data.tourActive) return;

        // Don't trigger if user is typing in an input field
        const target = e.target as HTMLElement;
        if (
            target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.isContentEditable
        ) {
            return;
        }

        // If already in auto mode, directly advance to next item
        if (this.$store.autoModeEnabled) {
            this.handleF1Press();
        } else {
            // Show confirmation modal when entering auto mode
            this.showAutoModeConfirmModal = true;
        }
    }

    hotKeyListener(e: KeyboardEvent) {
        // Both of these open something over the tour overlay.
        if (this.$root.$data.tourActive) return;

        if (e.code === 'F4') this.showPatientRecentlyViewedModal = true;
        if (e.code === 'F2') this.focusGlobalSearch();
    }

    focusGlobalSearch() {
        (this.$refs!.globalSearchField as HTMLInputElement)!.focus();
    }

    navigateGlobalSearch(direction: 'up' | 'down') {
        const globalSearch = this.$refs.globalSearch as any;
        if (globalSearch) {
            globalSearch.navigate(direction);
        }
    }

    selectGlobalSearchItem() {
        const globalSearch = this.$refs.globalSearch as any;
        if (globalSearch) {
            globalSearch.selectCurrentItem();
        }
    }

    handleGlobalSearchSelected() {
        this.$root.$emit('bv::hide::popover');
        this.globalSearchSelectedIndex = -1;
        this.blurGlobalSearch();
    }

    handleEscapeKey() {
        if (this.globalQuery) {
            // Clear the search box if it has content
            this.globalQuery = '';
        } else {
            // Lose focus if search box is empty
            this.blurGlobalSearch();
        }
    }

    blurGlobalSearch() {
        (this.$refs!.globalSearchField as HTMLInputElement)?.blur();
    }



    @Watch('notificationCounter')
    notificationCountChange(newValue: number, oldValue: number) {
        if (newValue > oldValue) {
            this.notificationShouldShake = true;
            setTimeout(() => {
                this.notificationShouldShake = false;
            }, 800);
        }
    }

    capitalizeFirstLetter(str: string | null | undefined) {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    toggleNotificationDropDown() {
        this.activeNotifications = !this.activeNotifications;
    }

    closeDropDown() {
        this.activeNotifications = false;
    }

    toggleNavbar() {
        this.showNavbar = !this.showNavbar;
    }

    toggleProductivity() {
        if (getUserSetting("ViewProductivityStats") == 'true') {
            saveUserSetting("ViewProductivityStats", 'false');
            this.viewProductivity = false;
        } else {
            saveUserSetting("ViewProductivityStats", 'true');
            this.viewProductivity = true;
        }
    }

    /**
     * The technician error tiles report either the deduped per-prescription figures or the
     * per-event ones. A prescription carrying two errors counts once under the deduped math
     * and twice under per-event, so the two definitions disagree by design -- the tenant
     * chooses which its technicians see. The productivity score stays on the deduped count
     * either way, since it is computed server-side from Errors.
     */
    protected showErrorEventStats = false;

    private get technicianStats(): Partial<UserProductivity> {
        return this.userProductivity as Partial<UserProductivity>;
    }

    get scriptErrors(): number {
        const { errorEvents, errors } = this.technicianStats;
        return (this.showErrorEventStats ? errorEvents : errors) ?? 0;
    }

    get scriptsWithoutErrors(): number {
        const { netTotalErrorEvents, netTotal } = this.technicianStats;
        return (this.showErrorEventStats ? netTotalErrorEvents : netTotal) ?? 0;
    }

    get scriptErrorRate(): number {
        const { errorEventRate, error } = this.technicianStats;
        return (this.showErrorEventStats ? errorEventRate : error) ?? 0;
    }

    get scriptErrorsTooltip(): string {
        return this.showErrorEventStats ? "Script Errors (Per Event)" : "Script Errors";
    }

    /**
     * Per-event the tile shows Total - ErrorEvents, which is not the number of prescriptions
     * that came out clean: 5 scripts with both errors on one of them shows 3, though 4 were
     * error-free. Name the subtraction rather than keep a label the figure no longer supports.
     */
    get scriptsWithoutErrorsTooltip(): string {
        return this.showErrorEventStats ? "Scripts Less Error Events" : "Scripts Without Errors";
    }

    /**
     * Per-event this is errors per script typed, not the share of scripts carrying an error --
     * nothing bounds it at 100% before the server-side clamp.
     */
    get scriptErrorRateTooltip(): string {
        return this.showErrorEventStats ? "Errors Per Script %" : "Error %";
    }

    async fetchShowErrorEventStats() {
        try {
            const resp = await this.$http.get<boolean>("/Configuration/ShowTechnicianErrorEventStats");
            this.showErrorEventStats = resp.data === true;
        } catch (error) {
            // Fall back to the deduped figures -- what every tenant saw before this option existed.
            // Warn rather than fail silently: a persistent lookup failure otherwise pins the tenant
            // to the old math with no signal anywhere that the option is being ignored.
            console.warn("Could not read ShowTechnicianErrorEventStats; showing deduped error figures.", error);
            this.showErrorEventStats = false;
        }
    }

    getProductivity() {
        axios.get<ProductivityStatsBase>('reports/GetProductivityReport')
            .then(response => {
                let stats = response.data;
                if (stats.isPharmacist)
                    this.userProductivity = Object.assign(new PharmacistProductivity(), stats);
                else
                    this.userProductivity = Object.assign(new UserProductivity(), stats);
            })
            .catch(err => {
                console.error('Error while getting productivity stats', { err, response: err?.response });
            })
            .finally(() => this.isLoading = false);
    }

    async logOut() {
        if (this?.$config?.VITE_APP_USE_COGNITO == 'true') {
            logoutCognito();
        } else {
            // Routes through MicrosoftAuth so the silent-renewal timer and cached
            // token are cleared, not just the MSAL session.
            await MicrosoftAuth.logout();
        }
        userInfoService.clearUserData();
        this.$router.push("/login");
    }

    getCurrentPatientIdFromRoute(): number | undefined {
        if (this.$route.name === 'PatientDashboard' && this.$route.params.id) {
            return parseInt(this.$route.params.id as string, 10) || undefined;
        }
        return undefined;
    }

    protected dispenseErrorLabel = "";

    async fetchDispenseErrorLabel() {
        try {
            const resp = await this.$http.get("/Configuration/DispenseErrorLabel");
            this.dispenseErrorLabel = resp.data?.trim();
        } catch {
            this.dispenseErrorLabel = "Error";
            //ignore
        }
    }
};
</script>

<style>
.notification-popover {
    max-width: 400px !important;
    width: 400px;
}
</style>

<style scoped lang="scss">
.action-btn {
    cursor: pointer;
    padding: 7px 4px 4px;
    border: 1px solid transparent;
    border-radius: 6px;

    a {
        text-decoration: none;
    }

    &:has(.highlight-count) {
        background-color: rgba(0, 0, 0, .35);
        border-color: rgba(255, 255, 255, .2);
    }

    &.dispense-errors:has(.highlight-count) {
        background-color: rgb(233, 0, 0);
        border-color: rgb(233, 0, 0);
    }
}

.highlight-count {
    color: yellow !important;
}

.productivity-spinner {
    width: 1rem;
    height: 1rem;
}

.action-count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.7em;
    height: 1.7em;
    padding: 2px 6px;
    font-size: .75em;
    font-weight: 600;
    color: white;
    font-variant-numeric: tabular-nums;
    letter-spacing: .2px;

    &.danger {
        color: white;
    }
}

.task-barbell {
    display: flex;
    align-items: center;
    font-size: .75em;
    font-weight: 600;
    color: white;
    font-variant-numeric: tabular-nums;
    letter-spacing: .2px;
    cursor: pointer;
}


.barbell-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.7em;
    height: 1.7em;
    padding: 2px 6px;
    color: white;
    text-align: center;
    transition: opacity 0.2s;
}

.barbell-connector {
    width: 1px;
    height: 1em;
    background-color: rgba(white, .3);
}

.dimmed {
    opacity: 0.35;
}

#user-stats {
    height: 6em;
}

::v-deep(.container-fluid) {
    display: grid !important;
    flex-wrap: nowrap !important;
    gap: 10px;
    grid-template-columns: 1fr 300px 1fr;
}

.no-permissions ::v-deep(.container-fluid) {
    grid-template-columns: 1fr;
}

.search {
    font-size: 1.2em;
    background: rgba(255, 255, 255, .2);
    border-top-left-radius: 7px;
    border-bottom-left-radius: 7px;
    margin-bottom: 20px;

    i {
        margin: 0 10px;
    }

    input[type='search'] {
        background: rgba(255, 255, 255, .3);
        border: none;
        color: black;
        width: 100% !important;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }

    input[type='search']:focus {
        background: white;
        box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
    }

    input[type='search']::placeholder {
        color: #3c5068;
        font-size: 0.9em;
        font-weight: 600;
    }

    input[type='search']:focus::placeholder {
        color: #3c5068;
    }
}

.actions {
    display: flex;
    align-items: stretch;
    gap: 4px;
    text-align: center;
    padding-right: 12px;
    justify-content: flex-end;

    .fas,
    .far {
        font-size: 1.55em;
    }

    .dispense-errors,
    .notifications,
    .tasks {
        position: relative;
    }

    .tasks-icon,
    .dispense-error-notification-icon,
    .notification-icon {
        display: flex;
        flex-direction: column;
        align-items: center;
        height: 100%;
        gap: 2px;
    }

    .action-count-badge,
    .task-barbell {
        margin-top: auto;
    }

    button {
        padding: 0;
    }
}

#notification-bell {
    background: none;
    border: none;
    color: white;
}

.stats {
    display: flex;
    flex-wrap: wrap;
    gap: 0 2px;

    div {
        border-radius: 7px;
        padding: 4px 8px;
        transition: all .2s;
        border: 1px solid transparent;
    }

    div:hover {
        background: rgba(white, .15);
        border-color: rgba(white, .25);
    }

    i,
    svg {
        margin-right: 2px;
    }
}

.btn-link {
    margin-top: 4px;
    margin-bottom: 4px;
}

@keyframes prominent-shake {
    0% {
        transform: rotate(0deg) scale(1);
    }

    10% {
        transform: rotate(20deg) scale(1.1);
        text-shadow: 0 0 5px white;
    }

    20% {
        transform: rotate(-20deg) scale(1.1);
        text-shadow: 0 0 5px white;
    }

    30% {
        transform: rotate(20deg) scale(1.1);
        text-shadow: 0 0 5px white;
    }

    40% {
        transform: rotate(-20deg) scale(1.1);
        text-shadow: 0 0 5px white;
    }

    50% {
        transform: rotate(10deg) scale(1.05);
        text-shadow: 0 0 2px white;
    }

    60% {
        transform: rotate(-10deg) scale(1.05);
        text-shadow: 0 0 2px white;
    }

    70% {
        transform: rotate(10deg) scale(1.05);
        text-shadow: 0 0 2px white;
    }

    80% {
        transform: rotate(-10deg) scale(1.05);
        text-shadow: 0 0 2px white;
    }

    90% {
        transform: rotate(5deg) scale(1);
    }

    100% {
        transform: rotate(0deg) scale(1);
    }
}

.shake {
    animation: prominent-shake 0.8s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
}

.auto-mode-btn {
    button {
        background: transparent;
        border: none;
        color: white;
        padding: 0;
        cursor: pointer;
    }

    .auto-mode-icon {
        display: inline-block;
        transition: all 0.3s ease;

        i {
            font-size: 1.55em;
        }
    }

    .auto-mode-active {
        color: #ff6b35;
        filter: drop-shadow(0 0 3px rgba(255, 107, 53, 0.6));
        animation: auto-mode-pulse 2s ease-in-out infinite;
    }
}

@keyframes auto-mode-pulse {
    0% {
        transform: rotate(0deg) translate(0px, 0px);
        filter: drop-shadow(0 0 3px rgba(255, 107, 53, 0.6));
    }
    15% {
        transform: rotate(-3deg) translate(0.3px, -0.3px);
        filter: drop-shadow(0 0 3px rgba(255, 107, 53, 0.65));
    }
    25% {
        transform: rotate(2deg) translate(-0.2px, 0.2px);
        filter: drop-shadow(0 0 3px rgba(255, 107, 53, 0.7));
    }
    40% {
        transform: rotate(-4deg) translate(0.5px, -0.5px);
        filter: drop-shadow(0 0 4px rgba(255, 107, 53, 0.75));
    }
    55% {
        transform: rotate(3deg) translate(-0.4px, 0.4px);
        filter: drop-shadow(0 0 3px rgba(255, 107, 53, 0.7));
    }
    70% {
        transform: rotate(-2deg) translate(0.3px, -0.3px);
        filter: drop-shadow(0 0 3px rgba(255, 107, 53, 0.65));
    }
    85% {
        transform: rotate(1deg) translate(-0.1px, 0.1px);
        filter: drop-shadow(0 0 3px rgba(255, 107, 53, 0.7));
    }
    100% {
        transform: rotate(0deg) translate(0px, 0px);
        filter: drop-shadow(0 0 3px rgba(255, 107, 53, 0.6));
    }
}
</style>
